import { useQuery } from '@tanstack/react-query';
import { ExternalLink, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { api } from '../../../../api/client';

// Returns a small status indicator for a given count and the expected
// constraint. h1=1 wanted; h2 >=2 wanted; h3 >=1 wanted (light constraint).
function statusFor(name, count) {
  if (name === 'h1') {
    if (count === 1) return { icon: CheckCircle2, tone: 'emerald', label: 'OK' };
    if (count === 0) return { icon: XCircle, tone: 'rose', label: 'Missing' };
    return { icon: XCircle, tone: 'rose', label: 'Too many' };
  }
  if (name === 'h2') {
    if (count >= 2) return { icon: CheckCircle2, tone: 'emerald', label: 'OK' };
    if (count === 0) return { icon: XCircle, tone: 'rose', label: 'Missing' };
    return { icon: AlertTriangle, tone: 'amber', label: 'Add more' };
  }
  // h3
  if (count >= 1) return { icon: CheckCircle2, tone: 'emerald', label: 'OK' };
  return { icon: AlertTriangle, tone: 'amber', label: 'Add some' };
}

const TONE_CLASS = {
  emerald: 'text-emerald-700 bg-emerald-50',
  rose: 'text-rose-700 bg-rose-50',
  amber: 'text-amber-700 bg-amber-50',
};

export default function HeadingsFix({ domain, onFixApplied }) {
  const pagesQuery = useQuery({
    queryKey: ['pages', domain._id],
    queryFn: () => api.get(`/api/domains/${domain._id}/pages`).then((r) => r.data.pages),
  });

  if (pagesQuery.isPending) {
    return <div className="text-sm text-ink-400">Loading pages…</div>;
  }
  const pages = pagesQuery.data || [];
  const worst = pages
    .filter((p) => (p.audit_breakdown?.headingHierarchy ?? 0) < 10)
    .sort(
      (a, b) =>
        (a.audit_breakdown?.headingHierarchy ?? 0) -
        (b.audit_breakdown?.headingHierarchy ?? 0),
    )
    .slice(0, 8);

  if (worst.length === 0) {
    return (
      <div className="text-sm text-ink-500">
        No pages currently fail this check. Re-run the audit if you’ve recently changed your
        template headings.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-600">
        Each AI-crawlable page should have <strong>exactly one</strong>{' '}
        <code className="px-1 bg-gray-50 rounded">&lt;h1&gt;</code>, at least two{' '}
        <code className="px-1 bg-gray-50 rounded">&lt;h2&gt;</code> section headings, and
        ideally one or more <code className="px-1 bg-gray-50 rounded">&lt;h3&gt;</code>{' '}
        sub-headings. AI engines use this structure to extract sections.
      </p>

      <div className="space-y-2">
        {worst.map((p) => {
          const counts = p.audit_heading_counts || { h1: 0, h2: 0, h3: 0 };
          let pathLabel = p.url;
          try {
            pathLabel = new URL(p.url).pathname || p.url;
          } catch {
            // leave as-is
          }
          return (
            <div key={p._id} className="rounded-xl border border-gray-100 p-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-ink-900">
                    <span className="truncate">{p.title || pathLabel}</span>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-ink-400 hover:text-brand-600 flex-none"
                      title="Open page"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="text-xs text-ink-400 truncate">{p.url}</div>
                </div>
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md flex-none">
                  {p.audit_breakdown?.headingHierarchy ?? 0} pts
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {['h1', 'h2', 'h3'].map((tag) => {
                  const s = statusFor(tag, counts[tag] || 0);
                  const Icon = s.icon;
                  return (
                    <span
                      key={tag}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium ${TONE_CLASS[s.tone]}`}
                    >
                      <Icon className="w-3 h-3" />
                      &lt;{tag}&gt;: {counts[tag] || 0} — {s.label}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <details>
        <summary className="cursor-pointer text-xs text-emerald-800 hover:text-emerald-900 font-medium select-none">
          How to fix heading hierarchy
        </summary>
        <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-xs text-ink-600 bg-emerald-50/60 border border-emerald-100 rounded-lg p-3">
          <li>
            Open the offending page template. Find every{' '}
            <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;h1&gt;</code> tag.
          </li>
          <li>
            Keep exactly one <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;h1&gt;</code> (your main page title); downgrade any extras to{' '}
            <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;h2&gt;</code>.
          </li>
          <li>
            Mark each major section heading as <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;h2&gt;</code>, and sub-section
            headings as <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;h3&gt;</code>. Don’t skip levels (no <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;h1&gt;</code> → <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;h4&gt;</code>).
          </li>
          <li>Deploy and re-run the audit.</li>
        </ol>
      </details>

      <div className="flex justify-end pt-2 border-t border-gray-100">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => onFixApplied?.()}
        >
          I’ve made the changes
        </button>
      </div>
    </div>
  );
}
